import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Copy, Check, Users as UsersIcon, Mail } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

const ROLES = [
  { value: "admin", label: "Admin", desc: "Full access — manage org, members, ledger, settings." },
  { value: "treasurer", label: "Treasurer", desc: "Record transactions, manage donors, post deposits, reports." },
  { value: "counter", label: "Counter", desc: "Mobile portal: record offering counts, sign and submit." },
  { value: "viewer", label: "Viewer", desc: "Read-only — dashboards and reports." },
];

const ROLE_BADGE = {
  admin: "bg-primary text-primary-foreground",
  treasurer: "bg-accent text-accent-foreground",
  counter: "bg-secondary text-secondary-foreground",
  viewer: "bg-muted text-muted-foreground",
};

export default function TeamPage() {
  const { activeOrgId, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "counter" });
  const [submitting, setSubmitting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [copied, setCopied] = useState("");
  const [shareLink, setShareLink] = useState(null);

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const fetchAll = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const [m, i] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/members`),
        api.get(`/organizations/${activeOrgId}/invites`),
      ]);
      setMembers(m.data);
      setInvites(i.data);
    } catch (e) {
      // viewers/counters get 403 on invites; that's fine
      try {
        const m = await api.get(`/organizations/${activeOrgId}/members`);
        setMembers(m.data);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [activeOrgId]);

  const sendInvite = async () => {
    if (!inviteForm.email) return toast.error("Email is required");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/organizations/${activeOrgId}/invites`, inviteForm);
      const link = `${baseUrl}/accept-invite/${data.token}`;
      setShareLink(link);
      setInviteForm({ email: "", role: "counter" });
      toast.success("Invite created — copy the link to share");
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const copyLink = async (link, key) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(key);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(""), 1800);
    } catch { toast.error("Copy failed — select link and copy manually"); }
  };

  const revokeInvite = async (inv) => {
    try {
      await api.delete(`/organizations/${activeOrgId}/invites/${inv.id}`);
      toast.success("Invite revoked");
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeRole = async (m, role) => {
    if (m.role === role) return;
    try {
      await api.patch(`/organizations/${activeOrgId}/members/${m.id}`, { role });
      toast.success(`${m.name || m.email} → ${role}`);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeMember = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/members/${removeTarget.id}`);
      toast.success("Member removed");
      setRemoveTarget(null);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); setRemoveTarget(null); }
  };

  return (
    <div className="space-y-8 fade-up">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Team</div>
          <h1 className="font-serif text-5xl tracking-tight mt-2">Members & invitations</h1>
          <p className="text-muted-foreground mt-2 max-w-md">
            Invite teammates with the right level of access — admins manage everything, treasurers post transactions, counters use the mobile portal, viewers see reports only.
          </p>
        </div>
        <Button onClick={() => { setShareLink(null); setInviteOpen(true); }} className="h-11 rounded-xl" data-testid="invite-member-button">
          <Plus className="w-4 h-4 mr-2" /> Invite member
        </Button>
      </header>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : (
        <>
          <Card className="rounded-2xl border-border shadow-none overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <UsersIcon className="w-4 h-4 text-muted-foreground" />
              <div className="text-sm font-semibold">Members · {members.length}</div>
            </div>
            <div className="divide-y divide-border">
              {members.map((m) => (
                <div key={m.id} className="px-5 py-4 flex items-center gap-4 flex-wrap" data-testid={`member-row-${m.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate flex items-center gap-2">
                      {m.name || m.email}
                      {m.is_owner && <Badge variant="outline" className="text-[10px]">Owner</Badge>}
                      {m.id === user?.id && <Badge variant="outline" className="text-[10px]">You</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.is_owner ? (
                      <Badge className={`${ROLE_BADGE[m.role]} rounded-lg`}>{m.role}</Badge>
                    ) : (
                      <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                        <SelectTrigger className="w-36 rounded-xl h-9" data-testid={`role-select-${m.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {!m.is_owner && m.id !== user?.id && (
                      <Button variant="ghost" size="icon" className="rounded-xl text-destructive hover:bg-destructive/10" onClick={() => setRemoveTarget(m)} data-testid={`remove-member-${m.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {invites.length > 0 && (
            <Card className="rounded-2xl border-border shadow-none overflow-hidden">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <div className="text-sm font-semibold">Pending & past invites</div>
              </div>
              <div className="divide-y divide-border">
                {invites.map((inv) => {
                  const link = `${baseUrl}/accept-invite/${inv.token}`;
                  const isPending = inv.status === "pending";
                  return (
                    <div key={inv.id} className="px-5 py-4 flex items-center gap-4 flex-wrap" data-testid={`invite-row-${inv.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{inv.email}</div>
                        <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-2 items-center">
                          <Badge className={`${ROLE_BADGE[inv.role]} rounded-lg text-[10px]`}>{inv.role}</Badge>
                          <span>{inv.status === "pending" ? "Pending" : inv.status === "accepted" ? `Accepted ${formatDate(inv.accepted_at)}` : "Revoked"}</span>
                          <span>· created {formatDate(inv.created_at)}</span>
                        </div>
                      </div>
                      {isPending && (
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" className="rounded-xl" onClick={() => copyLink(link, inv.id)} data-testid={`copy-invite-${inv.id}`}>
                            {copied === inv.id ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                            {copied === inv.id ? "Copied" : "Copy link"}
                          </Button>
                          <Button variant="ghost" size="icon" className="rounded-xl text-destructive hover:bg-destructive/10" onClick={() => revokeInvite(inv)} data-testid={`revoke-invite-${inv.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}

      <Dialog open={inviteOpen} onOpenChange={(o) => { setInviteOpen(o); if (!o) setShareLink(null); }}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">Invite to the team</DialogTitle>
            <DialogDescription>
              Send an invite link — the recipient sets their password on first visit.
            </DialogDescription>
          </DialogHeader>

          {!shareLink ? (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Email <span className="text-destructive">*</span></Label>
                <Input data-testid="invite-email-input" type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="name@church.org" className="rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        <div>
                          <div className="font-medium">{r.label}</div>
                          <div className="text-xs text-muted-foreground">{r.desc}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="ghost" className="rounded-xl" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button onClick={sendInvite} disabled={submitting} className="rounded-xl soft-glow" data-testid="invite-send-button">
                  {submitting ? "Creating…" : "Create invite link"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Share this link</div>
                <div className="mt-2 font-mono text-sm break-all" data-testid="invite-share-link">{shareLink}</div>
              </div>
              <p className="text-sm text-muted-foreground">
                Send this link to the invitee. They'll be guided to set a password and join your organization with the assigned role.
              </p>
              <DialogFooter className="gap-2">
                <Button variant="outline" className="rounded-xl" onClick={() => copyLink(shareLink, "new")} data-testid="invite-copy-button">
                  {copied === "new" ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied === "new" ? "Copied" : "Copy link"}
                </Button>
                <Button className="rounded-xl" onClick={() => { setShareLink(null); setInviteOpen(false); }}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}
        title={`Remove ${removeTarget?.name || removeTarget?.email}?`}
        description="They will lose access to this organization. You can re-invite them later."
        confirmLabel="Remove"
        onConfirm={removeMember}
        testid="confirm-remove-member"
      />
    </div>
  );
}
